/* Per-layer output schemas for structured output, derived from the governed
   schema. Structured output does not accept every JSON-Schema keyword, so the
   validation-only ones are removed — by keyword, never by property name: a
   property called "description" is data and must survive. The gates
   re-validate against the full schema afterwards regardless. */
import schema from '../narrative-unit.schema.json' with { type: 'json' };
import type { LayerKey } from './gates.ts';

const ANNOTATION_KEYWORDS = new Set(['description', 'title', '$id', '$schema', 'minLength', 'pattern', 'minimum']);

function strip(node: unknown, inProperties = false): unknown {
  if (Array.isArray(node)) return node.map(n => strip(n));
  if (node && typeof node === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      // Inside a `properties` map every key is a property name, so keep it.
      if (!inProperties && ANNOTATION_KEYWORDS.has(k) && (typeof v !== 'object' || v === null)) continue;
      out[k] = strip(v, k === 'properties' || k === 'definitions');
    }
    return out;
  }
  return node;
}

const root = schema as unknown as { definitions: Record<string, unknown>; properties: Record<string, unknown> };
const defs = strip(root.definitions, true) as Record<string, unknown>;
const props = strip(root.properties, true) as Record<string, unknown>;
const withDefs = (s: unknown) => ({ ...(s as Record<string, unknown>), definitions: defs });

export const LAYER_SCHEMAS: Record<LayerKey, Record<string, unknown>> = {
  meaning: withDefs(props.meaning),
  landscape: withDefs(props.landscape),
  architecture: withDefs(props.architecture),
  pathways: withDefs(props.pathways),
  emerging_issues: withDefs(props.emerging_issues),
  transformation_opportunities: withDefs(props.transformation_opportunities),
  dark_spots: { type: 'object', additionalProperties: false, required: ['dark_spots'], properties: { dark_spots: props.dark_spots } },
};

/* One schema for every pass, so the request prefix — and therefore the prompt
   cache — is identical across the chain. Each layer is a nullable property;
   the model fills the one it is asked for and leaves the rest null. The gates
   still validate the assembled unit against the full schema. */
const nullable = (schema: unknown) => ({ anyOf: [schema, { type: 'null' }] });
const bare = (key: LayerKey) => { const { definitions: _d, ...rest } = LAYER_SCHEMAS[key] as Record<string, unknown> & { definitions?: unknown }; return rest; };
export const UNION_SCHEMA: Record<string, unknown> = {
  type: 'object',
  additionalProperties: false,
  required: ['layer', 'meaning', 'landscape', 'architecture', 'pathways', 'emerging_issues', 'transformation_opportunities', 'dark_spots'],
  properties: {
    layer: { type: 'string', enum: ['meaning', 'landscape', 'architecture', 'pathways', 'emerging_issues', 'transformation_opportunities', 'dark_spots'] },
    meaning: nullable(bare('meaning')),
    landscape: nullable(bare('landscape')),
    architecture: nullable(bare('architecture')),
    pathways: nullable(bare('pathways')),
    emerging_issues: nullable(bare('emerging_issues')),
    transformation_opportunities: nullable(bare('transformation_opportunities')),
    dark_spots: nullable(props.dark_spots),
  },
  definitions: defs,
};
